import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { ContentItemTypeEnum, ImageContentItemFormGroup } from '@examdojo/models/content-item';
import { QuestionEditingOptions } from '@examdojo/models/question-editor';
import {
  ContentItemFormGroup,
  ContentItemViewerComponent,
  TableContentItemFormGroup,
} from '@examdojo/question-content-item';
import { SelectorBadgeComponent } from '@examdojo/ui/selector-badge';
import isEqual from 'lodash/isEqual';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { shareOneReplay } from '@examdojo/core/rxjs';
import {
  ActiveMarkschemeEditorItem,
  ActiveMarkschemeEditorItemCoordinates,
  ActiveMarkschemeEditorSubItemCoordinates,
  MARKSCHEME_ITEM_TYPE_TO_COLOR,
  MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL,
  MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL,
  MarkschemeEditorItemEnum,
} from './markscheme-item.model';
import { createMarkGroupFormGroup } from './markscheme-markgroup';
import { MarkGroupFormGroup } from './markscheme-markgroup/markscheme-markgroup-editor.model';
import { MarkschemeMarkgroupFormViewerComponent } from './markscheme-markgroup/markscheme-markgroup-form-viewer.component';
import { MarkschemeFormGroup } from './markscheme.model';
import { WorkedSolutionFormCtrl } from './solution-path/solution-path.model';

@Component({
  selector: 'dojo-markscheme-editor',
  templateUrl: './markscheme-editor.component.html',
  styleUrl: './markscheme-editor.component.scss',
  imports: [
    MatTabGroup,
    MatTab,
    MatTabLabel,
    ContentItemViewerComponent,
    SelectorBadgeComponent,
    MarkschemeMarkgroupFormViewerComponent,
    MatTooltip,
    ButtonComponent,
    IconComponent,
  ],
  host: {
    class: 'h-full w-full',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarkschemeEditorComponent {
  readonly formCtrl = input.required<MarkschemeFormGroup>();
  readonly disabled = input.required<boolean>();
  readonly allowSolutionPathGeneration = input<boolean>(false);
  readonly editingOptions = input.required<QuestionEditingOptions>();

  private readonly formCtrl$ = toObservable(this.formCtrl);

  private readonly formCtrlErrors$ = this.formCtrl$.pipe(
    switchMap((formCtrl) => formCtrl.statusChanges.pipe(startWith(formCtrl.status))),
    distinctUntilChanged(),
    map((status) => (status === 'INVALID' ? this.formCtrl().errors : null)),
    shareOneReplay(),
  );

  readonly activeFormGroupItem = input.required<ActiveMarkschemeEditorItem | null>();

  readonly setActiveFormGroupItem = output<{
    item: ActiveMarkschemeEditorItem;
    coordinates: ActiveMarkschemeEditorItemCoordinates;
  }>();
  readonly generateSolutionPaths = output<void>();
  readonly editItem = output<{
    item: ImageContentItemFormGroup | TableContentItemFormGroup;
    coordinates: ActiveMarkschemeEditorSubItemCoordinates;
  }>();

  readonly shouldShowAllMarkGroups = computed(() =>
    (this.editingOptions().displayMarkschemeElements ?? ['all_mark_groups', 'solution_paths']).includes(
      'all_mark_groups',
    ),
  );
  readonly shouldShowSolutionPaths = computed(() =>
    (this.editingOptions().displayMarkschemeElements ?? ['all_mark_groups', 'solution_paths']).includes(
      'solution_paths',
    ),
  );

  readonly visibleTabCount = computed(() => {
    let count = 0;
    if (this.shouldShowAllMarkGroups()) {
      count++;
    }
    if (this.shouldShowSolutionPaths()) {
      count += this.formCtrl().controls.solution_paths.length;
    }
    return count;
  });

  readonly shouldHideTabHeader = computed(() => this.visibleTabCount() <= 1);

  private readonly structureChanged$ = toObservable(this.formCtrl).pipe(
    switchMap((formCtrl) => {
      return formCtrl.valueChanges.pipe(
        startWith(formCtrl.value),
        distinctUntilChanged((prev, curr) => {
          const prevSolutionPaths = prev?.solution_paths;
          const currSolutionPaths = curr?.solution_paths;
          const prevAllMarkGroups = prev?.all_mark_groups;
          const currAllMarkGroups = curr?.all_mark_groups;

          const isAllMarkGroupsEqual = prevAllMarkGroups?.length === currAllMarkGroups?.length;

          if (!isAllMarkGroupsEqual) {
            return false;
          }

          if (!prevSolutionPaths || !currSolutionPaths) {
            return prevSolutionPaths === currSolutionPaths;
          }

          const isSolutionPathsEqual =
            prevSolutionPaths.length === currSolutionPaths.length &&
            prevSolutionPaths.every((prevItem, index) => {
              const currItem = currSolutionPaths[index];
              return (
                prevItem?.mark_group_refs &&
                currItem?.mark_group_refs &&
                prevItem.mark_group_refs.length === currItem.mark_group_refs.length &&
                isEqual(prevItem.mark_group_refs, currItem.mark_group_refs)
              );
            });

          return isSolutionPathsEqual;
        }),
      );
    }),
  );

  private readonly markGroupsBySolutionPathIndex$ = this.structureChanged$.pipe(
    map(() => {
      const formCtrl = this.formCtrl();

      const solutionPaths = formCtrl.controls.solution_paths.controls;

      return solutionPaths.map((solutionPath) => {
        const markGroupRefs = solutionPath.controls.mark_group_refs.value;
        return markGroupRefs
          .map((markGroupId) => {
            const index = formCtrl.controls.all_mark_groups.controls.findIndex(
              (markGroup) => markGroup.getRawValue().id === markGroupId,
            );
            const control = formCtrl.controls.all_mark_groups.controls[index];
            return index !== -1 ? { control, allMarkGroupsIndex: index } : null;
          })
          .filter(isNotNullish);
      });
    }),
  );

  readonly state = connectState({
    markGroupsBySolutionPathIndex: this.markGroupsBySolutionPathIndex$,
    formCtrlErrors: this.formCtrlErrors$,
  });

  readonly MarkschemeEditorItemEnum = MarkschemeEditorItemEnum;
  readonly MARKSCHEME_ITEM_TYPE_TO_COLOR = MARKSCHEME_ITEM_TYPE_TO_COLOR;
  readonly MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL = MARKSCHEME_ITEM_TYPE_TO_SHORT_LABEL;
  readonly MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL = MARKSCHEME_ITEM_TYPE_TO_LONG_LABEL;

  setActiveMarkGroupFormGroupItem(formGroup: MarkGroupFormGroup, markGroupIndex: number) {
    // Check if this markgroup is already the active item to prevent deselection on repeated clicks
    if (this.activeFormGroupItem()?.ctrl === formGroup) {
      return;
    }

    const solutionPaths = this.formCtrl().controls.solution_paths.controls;
    const markGroupId = formGroup.value.id;

    const solutionPathCount = solutionPaths.reduce((acc, solutionPath) => {
      const markGroupRefs = solutionPath.controls.mark_group_refs.value;
      if (markGroupRefs.includes(markGroupId ?? '')) {
        return acc + 1;
      }
      return acc;
    }, 0);

    this.setActiveFormGroupItem.emit({
      item: {
        type: MarkschemeEditorItemEnum.MarkGroup,
        ctrl: formGroup,
        solutionPathsCountFC: new FormControl<string>(solutionPathCount.toString(), {
          nonNullable: true,
        }),
      },
      coordinates: {
        'markscheme.all_mark_groups': markGroupIndex,
      },
    });
  }

  addNewMarkGroup() {
    const newMarkGroup = createMarkGroupFormGroup(
      {
        id: `mg_${uuid().slice(0, 3)}`,
        topic_ids: [],
        mark_code: '',
        marks: 0,
        notes: [],
        criterion: [],
      },
      this.editingOptions(),
    );

    this.formCtrl().controls.all_mark_groups.push(newMarkGroup);

    this.setActiveMarkGroupFormGroupItem(newMarkGroup, this.formCtrl().controls.all_mark_groups.length - 1);
  }

  setActiveWorkedSolutionFormGroupItem(formGroup: WorkedSolutionFormCtrl, solutionPathIndex: number) {
    if (this.activeFormGroupItem()?.ctrl === formGroup) {
      return;
    }
    this.setActiveFormGroupItem.emit({
      item: {
        type: MarkschemeEditorItemEnum.WorkedSolution,
        ctrl: formGroup,
      },
      coordinates: {
        'markscheme.solution_paths': solutionPathIndex,
      },
    });
  }

  editWorkedSolutionContentItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    if (item.value.type === ContentItemTypeEnum.Image) {
      this.editItem.emit({
        item: item as ImageContentItemFormGroup,
        coordinates: { worked_solution: contentItemIndex },
      });
    } else if (item.value.type === ContentItemTypeEnum.Table) {
      this.editItem.emit({
        item: item as TableContentItemFormGroup,
        coordinates: { worked_solution: contentItemIndex },
      });
    }
  }

  handleSetActiveAndEditItem(
    event: {
      markGroup: MarkGroupFormGroup;
      contentItem: ImageContentItemFormGroup | TableContentItemFormGroup;
      criterionIndex: number;
    },
    markGroupIndex: number,
  ) {
    this.setActiveMarkGroupFormGroupItem(event.markGroup, markGroupIndex);

    // Then emit the content item for editing
    if (event.contentItem.value.type === ContentItemTypeEnum.Image) {
      this.editItem.emit({
        item: event.contentItem as ImageContentItemFormGroup,
        coordinates: { criterion: event.criterionIndex },
      });
    } else if (event.contentItem.value.type === ContentItemTypeEnum.Table) {
      this.editItem.emit({
        item: event.contentItem as TableContentItemFormGroup,
        coordinates: { criterion: event.criterionIndex },
      });
    }
  }
}
